/**
 * Why a change to a project tree was refused — a closed set, so a caller (the file tree, an agent's tool) can act on it:
 * - `invalid_path`: not a path the project can hold (empty, absolute, holding a NUL, climbing out with `..`), or a folder
 *   moved into itself;
 * - `invalid_name`: a name the editor does not make (`isValidFileName`);
 * - `exists`: the path is taken — a file or folder there in any letter case, or a file where a folder must be;
 * - `missing`: the file or folder acted on is not in the project;
 * - `protected`: the project cannot run without the file (or the folder holds one), or the sandbox always keeps it.
 */
export type ProjectRefusalCode = 'invalid_path' | 'invalid_name' | 'exists' | 'missing' | 'protected';

/**
 * Thrown for a change a project tree refuses: a path it cannot hold (empty, absolute, holding a NUL, or climbing out of
 * the project with `..`), or one it cannot take there. `reason` says why, `code` classifies it.
 */
export class InvalidProjectPathError extends Error {
  constructor(readonly path: string, readonly reason: string, readonly code: ProjectRefusalCode = 'invalid_path') {
    super(`Invalid project path "${path}": ${reason}`);
    this.name = 'InvalidProjectPathError';
  }
}

/**
 * A project path in its one spelling: posix separators, relative to the project root (a leading `/` or `./` is
 * dropped), no empty or `.` segments, and a `..` resolved against the folder before it. Refuses a path that is empty,
 * holds a NUL (no file system stores one), names a drive (`C:/x`, or the drive-relative `C:x`), or climbs out of the
 * project with `..`.
 */
export function normalizeProjectPath(raw: string): string {
  const trimmed = raw.trim().replace(/\\/g, '/');
  if (!trimmed) throw new InvalidProjectPathError(raw, 'it is empty');
  if (trimmed.includes('\0')) throw new InvalidProjectPathError(raw, 'it contains a NUL character');
  if (/^[A-Za-z]:/.test(trimmed)) throw new InvalidProjectPathError(raw, 'it is absolute');
  const segments: string[] = [];
  for (const segment of trimmed.split('/')) {
    if (segment === '' || segment === '.') continue;
    if (segment !== '..') segments.push(segment);
    else if (segments.pop() === undefined) throw new InvalidProjectPathError(raw, 'it climbs out of the project with ..');
  }
  if (!segments.length) throw new InvalidProjectPathError(raw, 'it names no file');
  return segments.join('/');
}
